import { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { plan_product_pricings_price_level_path } from '@/lib/routes'
import type { PriceLevel } from './PricingTable'

const DEFAULT_COLOR = '#cccccc'

interface Props {
  level: PriceLevel
  marketSlug: string
  planSlug: string
  channelSlug: string
  channelName: string
}

// One editable row inside ManagePriceLevelsModal: color + name, saved on
// Enter or the Save button, removed with a confirm. Local state mirrors the
// level prop and re-syncs after a save (the page's props refresh via the
// controller's redirect), so there is no separate "dirty" flag to clear.
export default function PriceLevelRow({ level, marketSlug, planSlug, channelSlug, channelName }: Props) {
  const [name, setName] = useState(level.name)
  const [color, setColor] = useState(level.color ?? DEFAULT_COLOR)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)

  useEffect(() => {
    setName(level.name)
    setColor(level.color ?? DEFAULT_COLOR)
  }, [level.name, level.color])

  const dirty = name.trim() !== level.name || color !== (level.color ?? DEFAULT_COLOR)

  function save() {
    if (!name.trim() || saving) return
    setSaving(true)
    router.patch(
      plan_product_pricings_price_level_path(marketSlug, planSlug, channelSlug, level.id),
      { price_level: { name: name.trim(), color } },
      { preserveScroll: true, onFinish: () => setSaving(false) },
    )
  }

  function remove() {
    if (
      !window.confirm(
        `Remove ${level.name} from ${channelName}? Prices entered under it and calendar days marked with it will be deleted.`,
      )
    ) {
      return
    }
    setRemoving(true)
    router.delete(plan_product_pricings_price_level_path(marketSlug, planSlug, channelSlug, level.id), {
      preserveScroll: true,
      onFinish: () => setRemoving(false),
    })
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={color}
        onChange={(e) => setColor(e.target.value)}
        aria-label={`${level.name} color`}
        disabled={removing}
        className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1"
      />
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save()
        }}
        disabled={saving || removing}
        className="flex-1"
      />
      {dirty && (
        <Button size="sm" onClick={save} disabled={!name.trim() || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={remove}
        disabled={removing}
        className="shrink-0 text-muted-foreground hover:text-destructive"
      >
        {removing ? 'Removing…' : 'Remove'}
      </Button>
    </div>
  )
}
